
import axios from 'axios';

const API_URL =
    (import.meta?.env?.VITE_API_URL || process.env.VUE_APP_API_URL) ||
    'https://metersuite.wtmeters.it/api';

const axiosInstance = axios.create({
    baseURL: API_URL,
    timeout: 20000
});

axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        config.headers['Content-Type'] = 'application/json';
        return config;
    },
    (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            console.error('Authentication error:', error.response.data);
        }
        return Promise.reject(error);
    }
);

class DeviceApiService {
    async getDevices(params = {}) {
        try {
            // Convert deviceTypes to advancedFilters if needed
            let advancedFilters = params.advancedFilters || {};

            // If we have deviceTypes but no advancedFilters, convert deviceTypes to advancedFilters
            if (params.deviceTypes && params.deviceTypes.length > 0 && Object.keys(advancedFilters).length === 0) {
                // Create empty filter options for each device type
                params.deviceTypes.forEach(type => {
                    advancedFilters[type] = {
                        // Common options
                        hasMpid: '',
                        hasReadings: '',

                        // Type-specific options
                        ...(type === 'mechanical' ? { hasRadio: '' } : {}),
                        ...(type !== 'mechanical' ? { isIntegrated: '' } : {}),
                        ...(type !== 'mechanical' ? { attachedToMeter: '' } : {}),
                        ...(type === 'lora_wbus' ? { deviceSubtype: '' } : {})
                    };
                });
            }

            const requestBody = {
                searchQuery: params.searchQuery || '',
                searchField: params.searchField || 'ldn',
                subFilter: params.subFilter || '',
                sortColumn: params.sortColumn || '',
                sortDirection: params.sortDirection || 'asc',
                advancedFilters: advancedFilters,
                currentPage: params.currentPage || 1,
                pageSize: params.pageSize || 10
            };

            console.log('Request body to API:', requestBody);

            const response = await axiosInstance.post('/devices/filter', requestBody);
            console.log('Response from POST /devices/filter:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error fetching devices:', error);
            throw error;
        }
    }

    async getAllDevicesForExport(params = {}) {
        try {
            // Convert deviceTypes to advancedFilters if needed (same logic as getDevices)
            let advancedFilters = params.advancedFilters || {};

            // If we have deviceTypes but no advancedFilters, convert deviceTypes to advancedFilters
            if (params.deviceTypes && params.deviceTypes.length > 0 && Object.keys(advancedFilters).length === 0) {
                // Create empty filter options for each device type
                params.deviceTypes.forEach(type => {
                    advancedFilters[type] = {
                        // Common options
                        hasMpid: '',
                        hasReadings: '',

                        // Type-specific options
                        ...(type === 'mechanical' ? { hasRadio: '' } : {}),
                        ...(type !== 'mechanical' ? { isIntegrated: '' } : {}),
                        ...(type !== 'mechanical' ? { attachedToMeter: '' } : {}),
                        ...(type === 'lora_wbus' ? { deviceSubtype: '' } : {})
                    };
                });
            }

            const requestBody = {
                searchQuery: params.searchQuery || '',
                searchField: params.searchField || 'ldn',
                subFilter: params.subFilter || '',
                sortColumn: params.sortColumn || '',
                sortDirection: params.sortDirection || 'asc',
                advancedFilters: advancedFilters
                // Note: NO pagination params for export - we want all data
            };

            console.log('Export request body to API:', requestBody);

            const response = await axiosInstance.post('/devices/export', requestBody, { timeout: 120000 });
            console.log('Response from POST /devices/export:', response.data);

            // Return just the devices array for the export
            return response.data.devices || [];
        } catch (error) {
            console.error('Error exporting devices:', error);
            throw error;
        }
    }

    async getDevice(ldn) {
        return (await axiosInstance.get(`/devices/${ldn}`)).data;
    }

    async createDevice(deviceData) {
        try {
            const formattedData = this.formatDeviceDataForBackend(deviceData);
            console.log('Formatted data being sent to backend:', formattedData);
            const response = await axiosInstance.post('/devices', formattedData);
            return response.data;
        } catch (error) {
            console.error('Full error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            throw error;
        }
    }

    formatDeviceDataForBackend(data) {
        const payload = {
            logical_device_number: data.LogicalDeviceNumber,
            is_integrated: !!data.IsIntegrated,
            type: data.Type?.toUpperCase() || '',
        };

        // Add protocol - convert empty to 'NONE'
        payload.protocol = data.Protocol?.toUpperCase() || 'NONE';

        // Add optional string fields only if they have values
        if (data.CustomerNumber) payload.customer_number = data.CustomerNumber;
        if (data.MPID) payload.mpid = data.MPID;
        if (data.LoRaEUI) payload.lora_eui = data.LoRaEUI;
        if (data.Caliber) payload.caliber = data.Caliber;
        if (data.IDRadio) payload.id_radio = data.IDRadio;

        // Add key fields only if they have values
        if (data.DLMSKey) payload.dlmskey = data.DLMSKey;
        if (data.LoRaKey) payload.lorakey = data.LoRaKey;
        if (data.WBusKey) payload.wbuskey = data.WBusKey;
        if (data.CTRKey) payload.ctrkey = data.CTRKey;

        // DO NOT send tenant_id - backend extracts it from JWT token

        return payload;
    }

    async updateDevice(ldn, deviceData) {
        return (await axiosInstance.put(`/devices/${ldn}`, deviceData)).data;
    }

    async deleteDevice(ldn) {
        await axiosInstance.delete(`/devices/${ldn}`);
    }

    async changeMPID(ldn, mpid) {
        return (await axiosInstance.put(`/devices/${ldn}/mpid`, { mpid })).data;
    }

    async attachMPID(ldn, mpid) {
        return (await axiosInstance.put(`/devices/${ldn}/mpid/attach`, { mpid })).data;
    }

    async removeFromMPID(ldn) {
        return (await axiosInstance.put(`/devices/${ldn}/mpid/remove`)).data;
    }

    async detachFromMPID(ldn) {
        return (await axiosInstance.put(`/devices/${ldn}/mpid/remove`)).data;
    }

    async installRadio(meterLdn, radioLdn) {
        return (await axiosInstance.put(`/devices/${meterLdn}/install-radio`, {
            radio_device_number: radioLdn,
            meter_logical_number: meterLdn
        })).data;
    }

    async uninstallRadio(meterLdn) {
        return (await axiosInstance.put(`/devices/${meterLdn}/uninstall-radio`)).data;
    }

    async removeDevice(ldn) {
        return (await axiosInstance.put(`/devices/${ldn}/remove`)).data;
    }

    async getSimStatus(ldn) {
        return (await axiosInstance.get(`/sim/devices/${ldn}`)).data;
    }

    async getSimHistory(ldn) {
        return (await axiosInstance.get(`/sim/devices/${ldn}/history`)).data;
    }

    async getDashboardStats() {
        return (await axiosInstance.get('/dashboard/stats')).data;
    }

}

export default new DeviceApiService();
